import { useCreateFolderVault } from '@/hooks/useVault';
import { CreateVaultDto } from '@/types/vault.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Plus, Shield, X } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const createFolderVaultSchema = z.object({
  name: z
    .string()
    .min(1, 'vault.validation.name.required')
    .max(100, 'vault.validation.name.maxLength'),
  description: z
    .string()
    .max(500, 'vault.validation.description.maxLength')
    .optional(),
});

type CreateFolderVaultFormData = z.infer<typeof createFolderVaultSchema>;

interface CreateFolderVaultModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName?: string;
}

export const CreateFolderVaultModal = ({
  isOpen,
  onClose,
  folderId,
  folderName,
}: CreateFolderVaultModalProps) => {
  const { t } = useTranslation();
  const createFolderVault = useCreateFolderVault(folderId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<CreateFolderVaultFormData>({
    resolver: zodResolver(createFolderVaultSchema),
  });

  // Pre-fill name with folder name when modal opens
  useEffect(() => {
    if (isOpen && folderName) {
      setValue('name', `Coffre-Fort - ${folderName}`);
      setValue(
        'description',
        'Coffre-fort pour les documents de l\'entreprise (DSN, éditions, bulletins combinés)',
      );
    }
  }, [isOpen, folderName, setValue]);

  const onSubmit = async (data: CreateFolderVaultFormData) => {
    try {
      const vaultData: CreateVaultDto = {
        name: data.name,
        description: data.description || undefined,
      };

      await createFolderVault.mutateAsync(vaultData);
      reset();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
            <Building2 className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t(
              'vault.createFolder.title',
              'Créer le Coffre-Fort du Dossier',
            )}
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            {folderName && (
              <span className="font-medium text-primary-600">
                {folderName}
              </span>
            )}
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            {t(
              'vault.createFolder.description',
              'Documents DSN, éditions et bulletins combinés',
            )}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name Field */}
          <div>
            <label
              htmlFor="name"
              className="mb-2 block text-sm font-medium text-neutral-700"
            >
              {t('vault.create.name', 'Nom du coffre')}
              <span className="text-error-600">*</span>
            </label>
            <input
              {...register('name')}
              type="text"
              id="name"
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
              placeholder={t(
                'vault.createFolder.namePlaceholder',
                'Coffre-Fort - Nom de l\'entreprise',
              )}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-error-600">
                {t(errors.name.message as string)}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label
              htmlFor="description"
              className="mb-2 block text-sm font-medium text-neutral-700"
            >
              {t('vault.create.description', 'Description')}
              <span className="ml-1 text-sm font-normal text-neutral-500">
                ({t('common.optional', 'Optionnel')})
              </span>
            </label>
            <textarea
              {...register('description')}
              id="description"
              rows={3}
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none resize-none"
              placeholder={t(
                'vault.createFolder.descriptionPlaceholder',
                'Coffre-fort pour les documents de l\'entreprise...',
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-error-600">
                {t(errors.description.message as string)}
              </p>
            )}
          </div>

          {/* Info Banner */}
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <div className="flex items-start gap-3">
              <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" />
              <div className="text-sm text-primary-800">
                <p className="font-medium">
                  {t('vault.createFolder.infoTitle', 'Coffre-fort d\'entreprise')}
                </p>
                <p className="mt-1">
                  {t(
                    'vault.createFolder.info',
                    'Les responsables et RH pourront être invités à accéder à ce coffre.',
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {isSubmitting
                ? t('common.creating', 'Création...')
                : t('vault.createFolder.submit', 'Créer le coffre')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
